/**
 * Product Quantity Limit Manager
 * Handles quantity limits and validation for products with custom.limit_per_order metafield
 * Works for all products (pre-order or regular) that have the limit_per_order metafield set
 */

// console.log('%c🚀 PRE-ORDER MANAGER LOADED', 'background: #222; color: #bada55; font-size: 16px; font-weight: bold; padding: 4px 8px;');
// console.log('%cThis script prevents adding items over the limit_per_order', 'color: #888; font-size: 12px;');

class PreOrderManager {
  constructor() {
    // console.log('🎬 PreOrderManager constructor called');
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializePreOrder());
    } else {
      this.initializePreOrder();
    }
  }

  initializePreOrder() {
    // console.log('⚙️ Initializing PreOrderManager...');
    // console.log('📍 Current page:', window.location.pathname);
    // console.log('📦 DOM ready state:', document.readyState);
    
    this.disableOverLimitButtons(); // Disable buttons immediately if limit reached
    this.updateMaxAttributes();
    this.updateButtonState();
    this.enforceQuantityLimits();
    this.addFormValidation();
    this.addDelegatedFormValidation(); // Add event delegation for dynamic forms
    this.observeQuantityChanges();
    this.listenToCartUpdates();
    this.observeDynamicContent(); // Watch for Quick View and other dynamic content
    
    // console.log('✅ PreOrderManager initialization complete');
  }

  observeDynamicContent() {
    const self = this;
    // console.log('👀 Setting up MutationObserver for dynamic content');
    
    // Watch for new nodes being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if this node or any child has limit inputs (both types)
            const quickAddInputs = node.querySelectorAll ? node.querySelectorAll('input[data-limit-per-order]') : [];
            const productPageInputs = node.querySelectorAll ? node.querySelectorAll('input[data-preorder-limit]') : [];
            
            const totalInputs = quickAddInputs.length + productPageInputs.length;
            
            if (totalInputs > 0) {
              // console.log('🆕 Dynamic content with limit inputs detected!', totalInputs, 'inputs');
              // console.log(`  - Quick Add: ${quickAddInputs.length}, Product Page/Quick View: ${productPageInputs.length}`);
              // Re-run button disabling for new content
              setTimeout(() => {
                self.disableOverLimitButtons();
              }, 200); // Small delay to ensure DOM is ready
            }
            
            // Also check if the node itself is a limit input
            if (node.hasAttribute && (node.hasAttribute('data-limit-per-order') || node.hasAttribute('data-preorder-limit'))) {
              // console.log('🆕 Dynamic limit input detected directly');
              setTimeout(() => {
                self.disableOverLimitButtons();
              }, 200);
            }
          }
        });
      });
    });
    
    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // console.log('✅ MutationObserver active');
  }

  disableOverLimitButtons() {
    const self = this;
    
    // Store cart state for synchronous checking
    if (!window.cartLimitState) {
      window.cartLimitState = {};
    }
    
    // Check cart and disable quick add buttons if limit is reached
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // console.log('🔍 Checking cart on page load:', cart.item_count, 'items');
        
        // Store cart quantities
        window.cartLimitState = {};
        cart.items.forEach(item => {
          window.cartLimitState[item.variant_id] = item.quantity;
        });
        
        // Find all quick add buttons with limits
        const quickAddInputs = document.querySelectorAll('input[data-limit-per-order]');
        
        quickAddInputs.forEach(input => {
          const limit = parseInt(input.getAttribute('data-limit-per-order'));
          const variantId = input.getAttribute('data-variant-id');
          
          const cartQuantity = window.cartLimitState[variantId] || 0;
          
          // console.log(`[Quick Add] Variant ${variantId}: ${cartQuantity}/${limit}`);
          
          // Find the form and button - but DON'T modify button to preserve AJAX
          const form = input.closest('form');
          if (form) {
            const button = form.querySelector('button[type="submit"]');
            if (button) {
              // Update button VISUAL state only (don't touch disabled attribute)
              if (cartQuantity >= limit) {
                // console.log(`🚫 Visually blocking button for variant ${variantId} (${cartQuantity}/${limit})`);
                button.style.opacity = '0.5';
                button.style.cursor = 'not-allowed';
                button.classList.add('limit-reached');
                
                const buttonText = button.querySelector('.product-card__atc-text');
                if (buttonText) {
                  buttonText.textContent = `Limit Reached (${limit}/${limit})`;
                }
              } else {
                // RE-ENABLE button visually
                // console.log(`✅ Re-enabling button for variant ${variantId} (${cartQuantity}/${limit})`);
                button.style.opacity = '';
                button.style.cursor = '';
                button.classList.remove('limit-reached');
                
                const buttonText = button.querySelector('.product-card__atc-text');
                if (buttonText) {
                  buttonText.textContent = 'Add to cart';
                }
              }
            }
          }
        });
        
        // Handle Product Page/Quick View buttons (data-preorder-limit)
        const productPageInputs = document.querySelectorAll('input[data-preorder-limit]');
        
        productPageInputs.forEach(input => {
          const limit = parseInt(input.getAttribute('data-preorder-limit'));
          const isPreOrder = input.hasAttribute('data-is-preorder');
          
          // Get variant ID from the form
          const form = input.closest('form');
          if (!form) return;
          
          const variantInput = form.querySelector('input[name="id"]');
          if (!variantInput) return;
          
          const variantId = variantInput.value;
          const cartQuantity = window.cartLimitState[variantId] || 0;
          
          // console.log(`[PDP] Checking variant ${variantId}: cart=${cartQuantity}, limit=${limit}`);
          
          const button = form.querySelector('button[type="submit"][name="add"]');
          if (button) {
            const buttonText = button.querySelector('.btn__text');
            
            // Store original button text and pre-order status
            if (buttonText && !buttonText.hasAttribute('data-original-text')) {
              buttonText.setAttribute('data-original-text', buttonText.textContent.trim());
            }
            if (isPreOrder) {
              button.setAttribute('data-is-preorder-product', 'true');
            }
            
            // Setup MutationObserver to watch button text changes
            if (!button.hasAttribute('data-observer-setup')) {
              const observer = new MutationObserver((mutations) => {
                // Use a flag to prevent infinite loops
                if (button.hasAttribute('data-updating')) return;
                
                mutations.forEach((mutation) => {
                  if (mutation.type === 'childList' || mutation.type === 'characterData') {
                    const currentText = buttonText.textContent.trim();
                    const originalText = buttonText.getAttribute('data-original-text');
                    const isLimitReached = button.classList.contains('limit-reached');
                    
                    // Only restore if: not limit reached, has original text, current text is different and not a limit message
                    if (!isLimitReached && originalText && currentText !== originalText && 
                        !currentText.includes('Limit Reached') && !currentText.includes('Add to cart')) {
                      button.setAttribute('data-updating', 'true');
                      buttonText.textContent = originalText;
                      setTimeout(() => button.removeAttribute('data-updating'), 50);
                    }
                  }
                });
              });
              
              observer.observe(buttonText, {
                childList: true,
                characterData: true,
                subtree: true
              });
              
              button.setAttribute('data-observer-setup', 'true');
            }
            
            if (cartQuantity >= limit) {
              // console.log(`🚫 LIMIT REACHED for variant ${variantId}`);
              button.style.opacity = '0.5';
              button.style.cursor = 'not-allowed';
              button.classList.add('limit-reached');
              
              if (buttonText) {
                if (isPreOrder) {
                  buttonText.textContent = `Pre-Order Limit Reached (${limit}/${limit})`;
                } else {
                  buttonText.textContent = `Limit Reached (${limit}/${limit})`;
                }
              }
            } else {
              // console.log(`✅ AVAILABLE for variant ${variantId} - restoring to: ${buttonText.getAttribute('data-original-text')}`);
              button.style.opacity = '';
              button.style.cursor = '';
              button.classList.remove('limit-reached');
              
              if (buttonText) {
                const originalText = buttonText.getAttribute('data-original-text');
                if (originalText) {
                  buttonText.textContent = originalText;
                }
              }
            }
          }
        });
        
        // Update cart state when cart changes
        self.watchCartChanges();
      })
      .catch(error => console.error('Error checking cart on load:', error));
  }

  watchCartChanges() {
    // Prevent duplicate subscriptions
    if (window.preorderCartWatcherSetup) return;
    window.preorderCartWatcherSetup = true;
    
    // Listen for cart updates and refresh our state
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
        // console.log('🔄 Cart updated, refreshing limit state');
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            window.cartLimitState = {};
            cart.items.forEach(item => {
              window.cartLimitState[item.variant_id] = item.quantity;
            });
            // console.log('📊 Updated cartLimitState:', window.cartLimitState);
            // Re-check all buttons
            this.disableOverLimitButtons();
          });
      });
      
      // Listen for variant changes
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
        // console.log('🔄 Variant changed, refreshing button state');
        setTimeout(() => {
          this.disableOverLimitButtons();
        }, 100);
      });
    }
    
    // Listen for cart drawer close
    document.addEventListener('cart-drawer:close', () => {
      // console.log('🚪 Cart drawer closed, refreshing state');
      setTimeout(() => {
        fetch('/cart.js')
          .then(res => res.json())
          .then(cart => {
            window.cartLimitState = {};
            cart.items.forEach(item => {
              window.cartLimitState[item.variant_id] = item.quantity;
            });
            // console.log('📊 Updated cartLimitState (drawer close):', window.cartLimitState);
            this.disableOverLimitButtons();
          });
      }, 500);
    });
    
    // Also listen for quantity changes in cart drawer
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver(() => {
        // console.log('👀 Cart drawer mutation detected');
        setTimeout(() => {
          fetch('/cart.js')
            .then(res => res.json())
            .then(cart => {
              window.cartLimitState = {};
              cart.items.forEach(item => {
                window.cartLimitState[item.variant_id] = item.quantity;
              });
              // console.log('📊 Updated cartLimitState (mutation):', window.cartLimitState);
              this.disableOverLimitButtons();
            });
        }, 300);
      });
      
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true
      });
    }
  }

  updateMaxAttributes() {
    // Update max attributes on product page inputs to reflect cart quantity
    const quantityInputs = document.querySelectorAll('input[data-preorder-limit]');
    
    quantityInputs.forEach(input => {
      const isInCart = input.closest('cart-items') !== null;
      
      if (!isInCart) {
        // Only update for product page, not cart
        const limit = parseInt(input.getAttribute('data-preorder-limit'));
        const cartQuantity = parseInt(input.getAttribute('data-cart-quantity')) || 0;
        const availableToAdd = Math.max(0, limit - cartQuantity);
        
        input.setAttribute('max', availableToAdd);
        
        // If current value exceeds available, adjust it
        const currentValue = parseInt(input.value) || 0;
        if (currentValue > availableToAdd) {
          input.value = availableToAdd;
        }
      }
    });
  }

  updateButtonState() {
    // Disable add to cart button if cart limit is reached
    const quantityInputs = document.querySelectorAll('input[data-preorder-limit]');
    
    quantityInputs.forEach(input => {
      const isInCart = input.closest('cart-items') !== null;
      
      if (!isInCart) {
        // Only for product page
        const limit = parseInt(input.getAttribute('data-preorder-limit'));
        const cartQuantity = parseInt(input.getAttribute('data-cart-quantity')) || 0;
        const isPreOrder = input.hasAttribute('data-is-preorder');
        
        // Find the add to cart button
        const form = input.closest('form[data-type="add-to-cart-form"]');
        if (form) {
          const addButton = form.querySelector('[name="add"]');
          const addButtonText = form.querySelector('[name="add"] > span');
          
          if (addButton && cartQuantity >= limit) {
            // Disable button and show message
            addButton.setAttribute('disabled', 'disabled');
            addButton.classList.add('disabled');
            
            if (addButtonText) {
              if (isPreOrder) {
                addButtonText.textContent = `Pre-Order Limit Reached (${limit}/${limit})`;
              } else {
                addButtonText.textContent = `Limit Reached (${limit}/${limit})`;
              }
            }
            
            // Set input value to 0
            if (input.value > 0) {
              input.value = 0;
            }
          }
        }
      }
    });
  }

  enforceQuantityLimits() {
    const quantityInputs = document.querySelectorAll('input[data-preorder-limit]');
    
    quantityInputs.forEach(input => {
      const limit = parseInt(input.getAttribute('data-preorder-limit'));
      
      // Check if already initialized
      if (input.hasAttribute('data-preorder-initialized')) return;
      input.setAttribute('data-preorder-initialized', 'true');
      
      const isInCart = input.closest('cart-items') !== null;
      
      // Enforce limit on input
      input.addEventListener('input', (e) => {
        const value = parseInt(e.target.value);
        const maxAllowed = this.getMaxAllowed(input, limit, isInCart);
        
        if (value > maxAllowed) {
          e.target.value = maxAllowed;
          this.showLimitMessage(input, limit, maxAllowed);
        }
      });

      // Enforce limit on change (for spinner buttons)
      input.addEventListener('change', (e) => {
        const value = parseInt(e.target.value);
        const maxAllowed = this.getMaxAllowed(input, limit, isInCart);
        
        if (value > maxAllowed) {
          e.target.value = maxAllowed;
          this.showLimitMessage(input, limit, maxAllowed);
          
          // Trigger cart update for cart drawer
          if (isInCart) {
            e.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

      // Prevent manual entry above limit
      input.addEventListener('keydown', (e) => {
        setTimeout(() => {
          const value = parseInt(e.target.value);
          const maxAllowed = this.getMaxAllowed(input, limit, isInCart);
          
          if (value > maxAllowed) {
            e.target.value = maxAllowed;
          }
        }, 0);
      });
    });
  }

  getMaxAllowed(input, limit, isInCart) {
    if (isInCart) {
      // For cart items, the limit is absolute
      return limit;
    } else {
      // For product page, consider what's already in cart
      const cartQuantity = parseInt(input.getAttribute('data-cart-quantity')) || 0;
      const availableToAdd = limit - cartQuantity;
      return Math.max(0, availableToAdd);
    }
  }

  showLimitMessage(input, limit, maxAllowed) {
    // Check if in cart (drawer or page)
    const isInCart = input.closest('cart-items') !== null;
    const container = isInCart 
      ? input.closest('.cart-item__action') 
      : input.closest('.product-form__quantity');
    
    if (!container) return;
    
    // Find or create message element
    let messageEl = container.querySelector('.preorder-limit-message');
    
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.className = 'preorder-limit-message';
      messageEl.style.cssText = 'color: #d32f2f; font-size: 11px; margin-top: 4px; line-height: 1.3;';
      
      if (isInCart) {
        // For cart (drawer or page), add after the quantity input container
        const quantityContainer = input.closest('.cart-quantity');
        if (quantityContainer && quantityContainer.parentNode) {
          quantityContainer.parentNode.insertBefore(messageEl, quantityContainer.nextSibling);
        }
      } else {
        container.appendChild(messageEl);
      }
    }

    // Show appropriate message
    if (isInCart) {
      messageEl.textContent = `Max ${limit} per order`;
    } else {
      const cartQuantity = parseInt(input.getAttribute('data-cart-quantity')) || 0;
      if (cartQuantity >= limit) {
        messageEl.textContent = `Limit reached (${limit} in cart)`;
      } else if (maxAllowed === 0) {
        messageEl.textContent = `Cannot add more (${cartQuantity}/${limit} in cart)`;
      } else {
        messageEl.textContent = `Max ${maxAllowed} more (${cartQuantity}/${limit} in cart)`;
      }
    }
    
    messageEl.style.display = 'block';
    
    // Hide message after 3 seconds
    setTimeout(() => {
      messageEl.style.display = 'none';
    }, 3000);
  }

  addDelegatedFormValidation() {
    // Store reference to this
    const self = this;
    
    // Intercept fetch API for cart/add.js
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      
      // Check if this is a cart add request
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // console.log('🛒 Intercepting fetch to', url);
        
        // Get form data from the request
        const config = args[1];
        if (config && config.body instanceof FormData) {
          const variantId = config.body.get('id');
          
          // console.log('Variant ID from request:', variantId);
          
          // Find if this variant has a limit
          const variantInput = document.querySelector(`input[data-variant-id="${variantId}"][data-limit-per-order]`);
          
          if (variantInput) {
            const limit = parseInt(variantInput.getAttribute('data-limit-per-order'));
            
            // console.log('🛑 FETCH INTERCEPTED - Limit:', limit, 'Variant:', variantId);
            
            // Check cart before allowing fetch (use originalFetch to avoid recursion)
            return originalFetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                let cartQuantity = 0;
                cart.items.forEach(item => {
                  if (item.variant_id.toString() === variantId.toString()) {
                    cartQuantity += item.quantity;
                  }
                });
                
                // console.log(`📊 Current cart: ${cartQuantity}/${limit}`);
                
                if (cartQuantity >= limit) {
                  // console.log('❌ LIMIT REACHED - Blocking fetch');
                  self.showLimitPopup(limit, cartQuantity);
                  
                  // Return a rejected promise to stop the add
                  return Promise.reject({
                    status: 'error',
                    message: `Limit of ${limit} per customer reached`,
                    description: `You already have ${cartQuantity} items in your cart.`
                  });
                } else {
                  // console.log('✅ LIMIT OK - Allowing fetch');
                  // Allow the original fetch to proceed
                  return originalFetch.apply(this, args);
                }
              })
              .catch(error => {
                if (error.status === 'error') {
                  // Our custom error, don't proceed
                  return Promise.reject(error);
                }
                // Other errors, allow fetch to proceed
                console.error('Error checking cart:', error);
                return originalFetch.apply(this, args);
              });
          }
        }
      }
      
      // Not a cart add or no limit, proceed normally
      return originalFetch.apply(this, args);
    };
    
    // Wait for custom elements to be defined
    if (customElements.get('product-form')) {
      this.interceptProductForm();
    } else {
      customElements.whenDefined('product-form').then(() => {
        this.interceptProductForm();
      });
    }
    
    // Override HTMLFormElement.prototype.submit for forms with limits
    const originalSubmit = HTMLFormElement.prototype.submit;
    HTMLFormElement.prototype.submit = function() {
      // console.log('Form.submit() called on:', this);
      
      // Check if this is an add-to-cart form with limit
      if (this.getAttribute('data-type') === 'add-to-cart-form') {
        const variantInput = this.querySelector('input[data-limit-per-order]');
        
        if (variantInput && !this.hasAttribute('data-limit-validated')) {
          // console.log('Intercepted submit(), validating first');
          // Trigger validation by dispatching submit event
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          this.dispatchEvent(submitEvent);
          return; // Don't call original submit
        }
      }
      
      // Call original submit
      originalSubmit.call(this);
    };
    
    // DON'T intercept form submit - let ProductForm handle it
    // Our fetch interception will catch the actual cart add request
    // console.log('✅ Form submit events will be handled by ProductForm (preserved AJAX)');
    // console.log('✅ Fetch interception will block over-limit requests');
  }

  addFormValidation() {
    // Add form submit validation
    const forms = document.querySelectorAll('form[data-type="add-to-cart-form"]');
    
    forms.forEach(form => {
      form.addEventListener('submit', (e) => {
        // Skip validation if already validated
        if (form.hasAttribute('data-limit-validated')) {
          form.removeAttribute('data-limit-validated');
          return true;
        }
        
        // Check for quantity input with limit (product page)
        const quantityInput = form.querySelector('input[data-preorder-limit]');
        
        if (quantityInput) {
          const limit = parseInt(quantityInput.getAttribute('data-preorder-limit'));
          const cartQuantity = parseInt(quantityInput.getAttribute('data-cart-quantity')) || 0;
          const quantity = parseInt(quantityInput.value) || 1;
          
          if (cartQuantity + quantity > limit) {
            e.preventDefault();
            this.showLimitMessage(quantityInput, limit, cartQuantity);
            return false;
          }
        } else {
          // Check for quick add form (product cards)
          const variantInput = form.querySelector('input[data-limit-per-order]');
          
          if (variantInput) {
            const limit = parseInt(variantInput.getAttribute('data-limit-per-order'));
            const variantId = variantInput.getAttribute('data-variant-id');
            
            // Prevent default and check cart
            e.preventDefault();
            e.stopImmediatePropagation();
            
            fetch('/cart.js')
              .then(response => response.json())
              .then(cart => {
                // console.log('Checking cart for variant:', variantId);
                let cartQuantity = 0;
                cart.items.forEach(item => {
                  if (item.variant_id.toString() === variantId.toString()) {
                    cartQuantity += item.quantity;
                    // console.log('Found in cart:', item.title, 'qty:', item.quantity);
                  }
                });
                
                // console.log(`Cart: ${cartQuantity}/${limit}`);
                
                if (cartQuantity >= limit) {
                  // Show error popup
                  // console.log('Limit reached, showing popup');
                  this.showLimitPopup(limit, cartQuantity);
                  return false;
                } else {
                  // Mark as validated and submit
                  // console.log('Limit OK, submitting form');
                  form.setAttribute('data-limit-validated', 'true');
                  
                  // Trigger submit with requestSubmit if available, otherwise submit()
                  if (form.requestSubmit) {
                    form.requestSubmit();
                  } else {
                    form.submit();
                  }
                }
              })
              .catch(error => {
                console.error('Error checking cart:', error);
                // Mark as validated and submit anyway if cart check fails
                form.setAttribute('data-limit-validated', 'true');
                if (form.requestSubmit) {
                  form.requestSubmit();
                } else {
                  form.submit();
                }
              });
            
            return false;
          }
        }
      });
    });
  }

  showFormError(form, message) {
    const errorContainer = form.querySelector('.product-form__error-message-wrapper');
    const errorMessage = form.querySelector('.product-form__error-message');
    
    if (errorContainer && errorMessage) {
      errorMessage.textContent = message;
      errorContainer.removeAttribute('hidden');
      errorMessage.removeAttribute('hidden');
      
      // Hide error after 5 seconds
      setTimeout(() => {
        errorContainer.setAttribute('hidden', '');
        errorMessage.setAttribute('hidden', '');
      }, 5000);
    }
  }

  observeQuantityChanges() {
    // Watch for dynamically added quantity inputs (for AJAX variant changes)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.enforceQuantityLimits();
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Listen for cart drawer opening
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer) {
      const drawerObserver = new MutationObserver(() => {
        if (!cartDrawer.hasAttribute('hidden')) {
          // Cart drawer is now visible, enforce limits
          setTimeout(() => {
            this.enforceQuantityLimits();
          }, 100);
        }
      });
      
      drawerObserver.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['hidden']
      });
    }
  }

  listenToCartUpdates() {
    // Listen for cart updates via FoxTheme pubsub
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (event) => {
        // console.log('Cart update event received via pubsub');
        // Wait for cart to update, then refresh product page states
        setTimeout(() => {
          // Fetch updated cart data and update data-cart-quantity attributes
          this.refreshCartQuantities();
        }, 500);
      });
    }

    // Also listen for native cart:updated event
    document.addEventListener('cart:updated', (event) => {
      // console.log('Cart updated event received');
      setTimeout(() => {
        this.refreshCartQuantities();
      }, 500);
    });
    
    // Also refresh on page load to ensure correct state
    setTimeout(() => {
      // console.log('Initial cart check on page load');
      this.refreshCartQuantities();
    }, 1000);
  }

  refreshCartQuantities() {
    // Fetch current cart and update data-cart-quantity on product pages
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        // console.log('Cart data received:', cart);
        
        // Update all preorder quantity inputs with new cart quantities
        const quantityInputs = document.querySelectorAll('input[data-preorder-limit]');
        
        quantityInputs.forEach(input => {
          const isInCart = input.closest('cart-items') !== null;
          
          if (!isInCart) {
            // Only update product page inputs
            let variantId = input.getAttribute('data-quantity-variant-id');
            
            // Fallback: try to get variant ID from the form
            if (!variantId) {
              const form = input.closest('form');
              const variantInput = form?.querySelector('input[name="id"]');
              if (variantInput) {
                variantId = variantInput.value;
              }
            }
            
            // console.log('Checking variant ID:', variantId);
            
            if (variantId) {
              // Find this variant in cart
              let cartQuantity = 0;
              cart.items.forEach(item => {
                if (item.variant_id.toString() === variantId.toString()) {
                  cartQuantity += item.quantity;
                  // console.log('Found in cart:', item.title, 'qty:', item.quantity);
                }
              });
              
              // console.log('Total cart quantity for variant:', cartQuantity);
              
              // Update the data attribute
              input.setAttribute('data-cart-quantity', cartQuantity);
              
              // Update max attribute and button state
              const limit = parseInt(input.getAttribute('data-preorder-limit'));
              const availableToAdd = Math.max(0, limit - cartQuantity);
              input.setAttribute('max', availableToAdd);
              
              // console.log(`Limit: ${limit}, Cart: ${cartQuantity}, Available: ${availableToAdd}`);
              
              // Update input value if it exceeds available
              const currentValue = parseInt(input.value) || 0;
              if (currentValue > availableToAdd) {
                input.value = availableToAdd;
              }
              
              // Update button state
              const form = input.closest('form[data-type="add-to-cart-form"]');
              if (form) {
                const addButton = form.querySelector('[name="add"]');
                const addButtonText = form.querySelector('[name="add"] > span.btn__text');
                
                // console.log('Button found:', addButton, 'Text found:', addButtonText);
                
                if (addButton && addButtonText) {
                  const isPreOrder = input.hasAttribute('data-is-preorder');
                  // console.log('Is Pre-Order:', isPreOrder);
                  
                  if (cartQuantity >= limit) {
                    // Disable button
                    // console.log('DISABLING BUTTON - Limit reached');
                    addButton.setAttribute('disabled', 'disabled');
                    addButton.classList.add('disabled');
                    if (isPreOrder) {
                      addButtonText.textContent = `Pre-Order Limit Reached (${limit}/${limit})`;
                    } else {
                      addButtonText.textContent = `Limit Reached (${limit}/${limit})`;
                    }
                  } else {
                    // Enable button
                    // console.log('ENABLING BUTTON - Can still add');
                    addButton.removeAttribute('disabled');
                    addButton.classList.remove('disabled');
                    if (isPreOrder) {
                      addButtonText.textContent = 'PRE-ORDER NOW';
                    } else {
                      addButtonText.textContent = 'Add to cart';
                    }
                  }
                }
              }
            } else {
              console.warn('No variant ID found for input:', input);
            }
          }
        });
      })
      .catch(error => console.error('Error fetching cart:', error));
  }

  interceptProductForm() {
    const self = this;
    // console.log('🔧 Intercepting ProductForm custom element');
    
    // Get all product-form elements
    const productForms = document.querySelectorAll('form[is="product-form"]');
    
    productForms.forEach(form => {
      // Store original addEventListener
      const originalAddEventListener = form.addEventListener.bind(form);
      
      // Override addEventListener to intercept submit handler
      form.addEventListener = function(type, listener, options) {
        if (type === 'submit') {
          // Wrap the original listener
          const wrappedListener = function(event) {
            // console.log('🎯 ProductForm submit intercepted');
            
            // Check for limit
            const variantInput = form.querySelector('input[data-limit-per-order]');
            
            if (variantInput && !form.hasAttribute('data-limit-validated')) {
              const limit = parseInt(variantInput.getAttribute('data-limit-per-order'));
              const variantId = variantInput.getAttribute('data-variant-id');
              
              // console.log('🛑 Checking limit before ProductForm processes');
              
              // Prevent the ProductForm from handling it
              event.preventDefault();
              event.stopImmediatePropagation();
              
              // Check cart
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  let cartQuantity = 0;
                  cart.items.forEach(item => {
                    if (item.variant_id.toString() === variantId.toString()) {
                      cartQuantity += item.quantity;
                    }
                  });
                  
                  // console.log(`📊 Cart check: ${cartQuantity}/${limit}`);
                  
                  if (cartQuantity >= limit) {
                    // console.log('❌ LIMIT REACHED in ProductForm');
                    self.showLimitPopup(limit, cartQuantity);
                  } else {
                    // console.log('✅ LIMIT OK, calling original handler');
                    form.setAttribute('data-limit-validated', 'true');
                    // Call original listener
                    listener.call(this, event);
                  }
                })
                .catch(error => {
                  console.error('Error:', error);
                  form.setAttribute('data-limit-validated', 'true');
                  listener.call(this, event);
                });
              
              return false;
            }
            
            // No limit or already validated, call original
            return listener.call(this, event);
          };
          
          originalAddEventListener(type, wrappedListener, options);
        } else {
          originalAddEventListener(type, listener, options);
        }
      };
    });
    
    // Also observe for dynamically added forms
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.matches && node.matches('form[is="product-form"]')) {
            // console.log('📍 New ProductForm detected, intercepting');
            // Apply interception to new form
            // (You would apply the same logic here)
          }
        });
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }

  showLimitPopup(limit, cartQuantity) {
    // Create popup overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fadeIn 0.2s ease;
    `;
    
    // Create popup content
    const popup = document.createElement('div');
    popup.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 32px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
      text-align: center;
      animation: slideUp 0.3s ease;
      position: relative;
    `;
    
    popup.innerHTML = `
      <style>
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      </style>
      <div style="width: 64px; height: 64px; margin: 0 auto 16px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#DC2626" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      </div>
      <h3 style="margin: 0 0 12px 0; font-size: 20px; font-weight: 700; color: #111;">Cannot Add More Items</h3>
      <p style="margin: 0 0 24px 0; color: #666; font-size: 14px; line-height: 1.6;">
        Limit is <strong>${limit}</strong> per customer.<br>
        You already have <strong>${cartQuantity}</strong> in your cart.
      </p>
      <button id="limit-popup-close" style="
        width: 100%;
        padding: 12px 24px;
        background: #000;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
      " onmouseover="this.style.background='#333'" onmouseout="this.style.background='#000'">
        Got it
      </button>
    `;
    
    overlay.appendChild(popup);
    document.body.appendChild(overlay);
    
    // Close popup handlers
    const closePopup = () => {
      overlay.style.animation = 'fadeOut 0.2s ease';
      setTimeout(() => {
        document.body.removeChild(overlay);
      }, 200);
    };
    
    document.getElementById('limit-popup-close').addEventListener('click', closePopup);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closePopup();
      }
    });
    
    // Add fadeOut animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
    `;
    document.head.appendChild(style);
  }
}

// Initialize Pre-Order Manager
new PreOrderManager();

// Cart validation (for theme's cart page)
if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
  FoxTheme.pubsub.subscribe('cart:update', (data) => {
    // Validate cart quantities against pre-order limits
    const cartItems = document.querySelectorAll('[data-preorder-limit]');
    cartItems.forEach(item => {
      const limit = parseInt(item.getAttribute('data-preorder-limit'));
      const quantityInput = item.closest('tr, .cart-item')?.querySelector('input[type="number"]');
      
      if (quantityInput) {
        const value = parseInt(quantityInput.value);
        if (value > limit) {
          quantityInput.value = limit;
          quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  });
}
